import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../../../auth/AuthContext'
import config from '../../../config'
import type {
    AdjuntoDTO,
    ClienteDTO,
    CreateAdjuntoRequest,
    CreateCRMRequest,
    CRMDTO,
    CRMTextoDTO,
    CRMTextoTipo,
    PagaebleResult,
    SearchPolizaDTO,
    SearchReciboDTO,
    SearchSiniestroDTO,
    StorageFile,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import * as Permisos from '../../../utils/Permisos'
import {
    daysFromToday,
    errorMessage,
    eventParts,
    firstFilled,
    isMoral,
    money,
    NOTE_TYPES,
    noteMeta,
    parseDate,
    personName,
    phoneHref,
    statusKey,
} from '../common/customer-common'
import type { BitacoraFilter } from '../common/customer-common'

export type ViewTab = 'resumen' | 'polizas' | 'primas' | 'siniestros' | 'docs' | 'bitacora'
export type SectionKey = 'polizas' | 'recibos' | 'siniestros' | 'bitacora' | 'adjuntos'
export type SectionStatus = 'idle' | 'loading' | 'ready' | 'denied' | 'error'
export type DocFilter = 'todos' | 'pdf' | 'img' | 'xls'
export type CountKey = 'polizas' | 'siniestros' | 'docs' | 'bitacora'

export interface SectionState {
    status: SectionStatus
    error: string | null
}

export interface UpcomingEvent {
    d: string
    m: string
    time: number
    t: string
    tone: string
}

const SECTION_ERROR = 'No se pudo cargar la información.'
const IDLE_SECTION: SectionState = { status: 'idle', error: null }
const INITIAL_SECTIONS: Record<SectionKey, SectionState> = {
    polizas: IDLE_SECTION,
    recibos: IDLE_SECTION,
    siniestros: IDLE_SECTION,
    bitacora: IDLE_SECTION,
    adjuntos: IDLE_SECTION,
}

const CLOSED_POLIZA = new Set(['cancelada', 'eliminada', 'terminada', 'vencida', 'renovada', 'precancelada'])
export const OPEN_POLIZA = new Set(['vigente', 'anticipada', 'gracia'])
export const PAID_RECIBO = ['pagado', 'conciliado']

export const fileExt = (name?: string | null) => String(name || '').split('.').pop()?.slice(0, 4).toUpperCase() || 'FILE'

export const isOverdue = (recibo: SearchReciboDTO) => {
    if (statusKey(recibo.status) !== 'pendiente') return false
    const days = daysFromToday(recibo.fecVence)
    return days != null && days < 0
}

export const lateDays = (recibo: SearchReciboDTO) => Math.max(0, -(daysFromToday(recibo.fecVence) ?? 0))

const readList = async <T,>(url: string): Promise<T[]> => {
    const response = await APIInvoke.invokeGET(url)
    const payload: WrapperResponse<T[] | PagaebleResult<T>> | null = await response.json()
    if (!payload?.ok) throw new Error(payload?.message || SECTION_ERROR)
    if (Array.isArray(payload.body)) return payload.body
    return payload.body?.items || []
}

export const useCustomerViewModel = () => {
    const { clienteId } = useParams<{ clienteId: string }>()
    const history = useHistory()
    const { canAccess } = useAuth()
    const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [cliente, setCliente] = useState<ClienteDTO | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [tab, setTab] = useState<ViewTab>('resumen')
    const [sections, setSections] = useState<Record<SectionKey, SectionState>>(INITIAL_SECTIONS)
    const [polizas, setPolizas] = useState<SearchPolizaDTO[]>([])
    const [recibos, setRecibos] = useState<SearchReciboDTO[]>([])
    const [siniestros, setSiniestros] = useState<SearchSiniestroDTO[]>([])
    const [bitacora, setBitacora] = useState<CRMTextoDTO[]>([])
    const [adjuntos, setAdjuntos] = useState<AdjuntoDTO[]>([])
    const [docFilter, setDocFilter] = useState<DocFilter>('todos')
    const [noteType, setNoteType] = useState<CRMTextoTipo>(NOTE_TYPES[0].id)
    const [note, setNote] = useState('')
    const [savingNote, setSavingNote] = useState(false)
    const [bitacoraFilter, setBitacoraFilter] = useState<BitacoraFilter>('todo')
    const [uploading, setUploading] = useState(false)
    const [toast, setToast] = useState<string | null>(null)

    const showToast = (message: string) => {
        clearTimeout(toastTimer.current)
        setToast(message)
        toastTimer.current = setTimeout(() => setToast(null), 4000)
    }

    useEffect(() => () => clearTimeout(toastTimer.current), [])

    useEffect(() => {
        let active = true
        const load = async () => {
            const fallback = 'No se pudo cargar el cliente.'
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/clientes/${clienteId}`)
                const payload: WrapperResponse<ClienteDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || fallback)
                setCliente(payload.body)
            } catch (loadError) {
                if (active) {
                    setCliente(null)
                    setError(errorMessage(loadError, fallback))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [clienteId, reloadToken])

    useEffect(() => {
        let active = true
        const setSection = (key: SectionKey, status: SectionStatus, sectionError: string | null = null) => {
            setSections((current) => ({ ...current, [key]: { status, error: sectionError } }))
        }
        const loadSection = async <T,>(key: SectionKey, allowed: boolean, load: () => Promise<T[]>, setItems: (items: T[]) => void) => {
            if (!allowed) {
                setItems([])
                setSection(key, 'denied')
                return
            }
            try {
                setSection(key, 'loading')
                const items = await load()
                if (!active) return
                setItems(items)
                setSection(key, 'ready')
            } catch (loadError) {
                if (active) setSection(key, 'error', errorMessage(loadError, SECTION_ERROR))
            }
        }
        setTab('resumen')
        setDocFilter('todos')
        setNoteType(NOTE_TYPES[0].id)
        setNote('')
        setBitacoraFilter('todo')
        loadSection('polizas', canAccess(Permisos.POLIZAS_R), () => readList<SearchPolizaDTO>(`/polizas?clienteId=${clienteId}&size=100&order=fechaVigenciaA asc`), setPolizas)
        loadSection('recibos', canAccess(Permisos.POLIZAS_R), () => readList<SearchReciboDTO>(`/recibos?cliente_id=${clienteId}&size=30&order=fecVence desc`), setRecibos)
        loadSection('siniestros', canAccess(Permisos.SINIESTROS_R), () => readList<SearchSiniestroDTO>(`/siniestros?clienteId=${clienteId}&size=50`), setSiniestros)
        loadSection('bitacora', canAccess(Permisos.BITACORA_R), async () => {
            const items = await readList<CRMDTO>(`/crm?clienteId=${clienteId}`)
            return items.flatMap((item) => item.textos || []).sort((left, right) => String(right.fecha || '').localeCompare(String(left.fecha || '')))
        }, setBitacora)
        loadSection('adjuntos', canAccess(Permisos.ADJUNTOS_R), () => readList<AdjuntoDTO>(`/adjuntos/clientes/${clienteId}`), setAdjuntos)
        return () => { active = false }
    }, [clienteId, reloadToken, canAccess])

    const addNote = async () => {
        const texto = note.trim()
        if (!texto || !cliente) return
        try {
            setSavingNote(true)
            const request: CreateCRMRequest = {
                asunto: 'bitacora',
                tipo: noteType,
                clienteId: cliente.id,
                textos: [{ tipo: noteType, texto }],
            }
            const response = await APIInvoke.invokePOST('/crm', request)
            const payload: WrapperResponse<CRMDTO> | null = await response.json()
            if (!payload?.ok) throw new Error(payload?.message || 'No se pudo guardar la bitácora.')
            const created = payload.body?.textos?.[0]
            if (created) setBitacora((items) => [created, ...items])
            setNote('')
            setBitacoraFilter('todo')
            showToast('Registro agregado a la bitácora')
        } catch (saveError) {
            showToast(errorMessage(saveError, 'No se pudo guardar la nota.'))
        } finally {
            setSavingNote(false)
        }
    }

    const openFile = async (path?: string | null) => {
        try {
            const url: string | null | undefined = await APIInvoke.resolveStoredFileUrl(path)
            if (url) window.open(url, '_blank', 'noopener')
        } catch (fileError) {
            showToast(errorMessage(fileError, 'No se pudo abrir el archivo.'))
        }
    }

    const uploadFiles = async (fileList: FileList | null) => {
        const files = Array.from(fileList || [])
        if (!files.length || !cliente) return
        try {
            setUploading(true)
            const created: AdjuntoDTO[] = []
            for (const file of files) {
                const body = new FormData()
                body.append('bucket', config.bucket.name)
                body.append('path', `clientes/${file.name}`)
                body.append('file', file)
                const uploaded: WrapperResponse<StorageFile> = await (await APIInvoke.uploadStorage(body)).json()
                if (!uploaded.ok || !uploaded.body) throw new Error(uploaded.message || 'No se pudo subir el archivo.')
                const request: CreateAdjuntoRequest = {
                    clienteId: cliente.id,
                    dominio: '',
                    path: uploaded.body.url,
                    archivo: file.name,
                }
                const saved: WrapperResponse<AdjuntoDTO> = await (await APIInvoke.invokePOST('/adjuntos', request)).json()
                if (!saved.ok || !saved.body) throw new Error(saved.message || 'No se pudo registrar el archivo.')
                created.push(saved.body)
            }
            setAdjuntos((items) => [...created, ...items])
            showToast(files.length === 1 ? 'Documento subido' : `${files.length} documentos subidos`)
        } catch (uploadError) {
            showToast(errorMessage(uploadError, 'No se pudo subir el archivo.'))
        } finally {
            setUploading(false)
        }
    }

    const editCliente = () => {
        if (!cliente) return
        history.push({ pathname: `/clientes/${cliente.id}/edit`, state: { backUrl: `/clientes/${cliente.id}` } })
    }

    const phone = firstFilled(cliente?.celulares?.map((item) => item?.numero) || [])
    const direccion = cliente?.direcciones?.[0]
    const sepomex = direccion?.sepomex
    const domicilio = [direccion?.direccion, direccion?.colonia, sepomex?.municipio, sepomex?.estado].filter(Boolean).join(', ')

    const vigentes = polizas.filter((item) => OPEN_POLIZA.has(statusKey(item.status)))
    const porRenovar = vigentes.filter((item) => {
        const days = daysFromToday(item.fechaVigenciaA)
        return days != null && days >= 0 && days <= 45
    })
    const pendientes = recibos.filter((item) => statusKey(item.status) === 'pendiente')
    const year = DateTime.now().year
    const currency = vigentes[0]?.moneda || 'MXN'

    const years = Object.entries(polizas.reduce<Record<string, number>>((groups, item) => {
        const itemYear = parseDate(item.fechaVigenciaDe)?.year
        if (!itemYear || CLOSED_POLIZA.has(statusKey(item.status))) return groups
        groups[itemYear] = (groups[itemYear] || 0) + Number(item.prima || 0)
        return groups
    }, {})).sort(([left], [right]) => Number(left) - Number(right)).slice(-5)

    const eventos = [
        ...vigentes.map((item): UpcomingEvent | null => {
            const parts = eventParts(item.fechaVigenciaA)
            if (!parts || (daysFromToday(item.fechaVigenciaA) ?? 0) < 0) return null
            const soon = porRenovar.some((poliza) => poliza.polizaId === item.polizaId)
            return { ...parts, t: `Renovación ${item.noPoliza || item.concepto || ''}`.trim(), tone: soon ? 'bg-tone-orange text-tone-orange-ink' : 'bg-k-line text-k-ink-2' }
        }),
        ...pendientes.map((item): UpcomingEvent | null => {
            const parts = eventParts(item.fecVence)
            if (!parts) return null
            return { ...parts, t: `Pago recibo ${item.serie || '—'}/${item.totalSerie || '—'} · ${money(item.total, currency)}`, tone: isOverdue(item) ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-accent-soft text-k-accent-ink' }
        }),
    ].filter((item): item is UpcomingEvent => item != null).sort((left, right) => left.time - right.time).slice(0, 3)

    const docFilters: Array<{ id: DocFilter; label: string }> = [
        { id: 'todos', label: `Todos (${adjuntos.length})` },
        { id: 'pdf', label: 'PDF' },
        { id: 'img', label: 'Imágenes' },
        { id: 'xls', label: 'Hojas' },
    ]
    const visibleDocs = adjuntos.filter((item) => {
        const ext = fileExt(item.archivo)
        if (docFilter === 'pdf') return ext === 'PDF'
        if (docFilter === 'img') return ['JPG', 'JPEG', 'PNG', 'JPE', 'WEBP'].includes(ext)
        if (docFilter === 'xls') return ext === 'XLS' || ext === 'XLSX'
        return true
    })

    const bitacoraNotas = bitacora.filter((item) => statusKey(item.tipo) !== 'adjunto')
    const counts: Record<CountKey, number | null> = {
        polizas: sections.polizas.status === 'ready' ? polizas.length : null,
        siniestros: sections.siniestros.status === 'ready' ? siniestros.length : null,
        docs: sections.adjuntos.status === 'ready' ? adjuntos.length : null,
        bitacora: sections.bitacora.status === 'ready' ? bitacoraNotas.length : null,
    }

    return {
        cliente,
        loading,
        error,
        tab,
        sections,
        polizas,
        recibos,
        siniestros,
        docFilter,
        noteType,
        note,
        savingNote,
        bitacoraFilter,
        uploading,
        toast,
        name: cliente ? personName(cliente) : '',
        moral: isMoral(cliente?.fiscal),
        phone,
        email: firstFilled(cliente?.correos?.map((item) => item?.correo) || []),
        whatsapp: phoneHref(phone),
        domicilioTexto: [domicilio, direccion?.cp ? `CP ${direccion.cp}` : ''].filter(Boolean).join(', '),
        sinceYear: parseDate(cliente?.fecRegistro)?.year,
        vigentes,
        activas: polizas.filter((item) => !CLOSED_POLIZA.has(statusKey(item.status))),
        porRenovar,
        canceladas: polizas.filter((item) => statusKey(item.status) === 'cancelada').length,
        primaAnual: vigentes.reduce((sum, item) => sum + Number(item.prima || 0), 0),
        vencidos: recibos.filter(isOverdue),
        saldo: pendientes.reduce((sum, item) => sum + Number(item.total || 0), 0),
        comision: recibos
            .filter((item) => PAID_RECIBO.includes(statusKey(item.status)) && parseDate(item.fecPago)?.year === year)
            .reduce((sum, item) => sum + Number(item.comision || 0), 0),
        year,
        currency,
        years,
        maxYear: Math.max(...years.map(([, value]) => value), 1),
        growth: years.length > 1 && years[0][1] > 0
            ? Math.round(((years[years.length - 1][1] - years[0][1]) / years[0][1]) * 100)
            : null,
        eventos,
        docFilters,
        visibleDocs,
        visibleNotes: bitacoraNotas.filter((item) => bitacoraFilter === 'todo' || noteMeta(item.tipo).filter === bitacoraFilter),
        notePlaceholder: NOTE_TYPES.find((item) => item.id === noteType)?.placeholder,
        counts,
        setTab,
        setDocFilter,
        setNoteType,
        setNote,
        setBitacoraFilter,
        dismissToast: () => setToast(null),
        reload: () => setReloadToken((token) => token + 1),
        addNote,
        openFile,
        uploadFiles,
        editCliente,
    }
}
